import { useState, useEffect } from "react";
import styles from "./CreateCommunityPage.module.css";
import placeholderImage from "../../assets/images/image-upload-placeholder.png";
import api from "../../services/api";
import { ENDPOINTS } from "../../utils/endpoints";
import useDashboardStore from "../../store/dashboardStore";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";

const CreateCommunityPage = () => {
  const { setIsAllCommunityScreen, editCommunityId } = useDashboardStore(); //To navigate to all community screen

  const [communityName, setCommunityName] = useState("");
  const [themeColor, setThemeColor] = useState("#3b0a59");
  const [hasLiveSession, setHasLiveSession] = useState(false);
  const [selectedSectionIds, setSelectedSectionIds] = useState([]);
  const [newSection, setNewSection] = useState("");
  const [sections, setSections] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [bannerFile, setBannerFile] = useState(null);
  const [iconFile, setIconFile] = useState(null);

  const [bannerPreview, setBannerPreview] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);

  // Loading states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreatingSection, setIsCreatingSection] = useState(false);

  // Rename functionality states
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editingSectionTitle, setEditingSectionTitle] = useState("");
  const [isRenamingSection, setIsRenamingSection] = useState(false);

  // NEW: Delete functionality states
  const [deletingSectionId, setDeletingSectionId] = useState(null);

  const handleCheckboxChange = (section) => {
    setSelectedSectionIds((prev) =>
      prev.includes(section.id)
        ? prev.filter((s) => s !== section.id)
        : [...prev, section.id]
    );
  };

  const handleBannerUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBannerFile(file);
      setBannerPreview(URL.createObjectURL(file));
    }
  };

  const handleIconUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIconFile(file);
      setIconPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async () => {
    // Prevent double clicks and submissions while already submitting
    if (isSubmitting) return;

    if (!communityName || !themeColor || !selectedSectionIds.length) {
      toast.error("Please fill all fields.");
      return;
    }

    //For Create banner and icon is mandatory
    if (editCommunityId === null) {
      if (!bannerFile || !iconFile) {
        toast.error("Please upload both images.");
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("name", communityName);
      formData.append("theme_color", themeColor);
      formData.append(
        "has_live_session",
        hasLiveSession === true ? true : false
      );
      formData.append("section_ids", JSON.stringify(selectedSectionIds));
      formData.append("banner", bannerFile);
      formData.append("icon", iconFile);

      const { data } =
        editCommunityId !== null
          ? await api.put(
              ENDPOINTS.UPDATE_COMMUNITY_BY_ID(editCommunityId),
              formData
            )
          : await api.post(ENDPOINTS.CREATE_COMMUNITY, formData);

      if (data.success) {
        toast.success(data.message || "Community created successfully");
        setIsAllCommunityScreen();
      } else {
        toast.error(data.message || "Something went wrong");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error creating community");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateSection = async () => {
    // Prevent double clicks and submissions while already creating
    if (isCreatingSection || !newSection.trim()) return;

    setIsCreatingSection(true);

    try {
      const payload = {
        title: newSection,
      };

      const { data } = await api.post(ENDPOINTS.CREATE_SECTION, payload);
      if (data.success) {
        const res = await api.get(ENDPOINTS.GET_ALL_SECTIONS);
        if (res.data.success) {
          setSections(res.data.sections);
          const newSectionCreated = res.data.sections.find(
            (s) => s.title === newSection
          );
          if (newSectionCreated) {
            handleCheckboxChange(newSectionCreated);
          }
          setNewSection("");
        }
      } else {
        toast.error(data.message || "Something went wrong");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error creating section");
    } finally {
      setIsCreatingSection(false);
    }
  };

  // Start editing a section
  const handleStartEdit = (section) => {
    setEditingSectionId(section.id);
    setEditingSectionTitle(section.title);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingSectionId(null);
    setEditingSectionTitle("");
  };

  // Save renamed section
  const handleSaveRename = async (sectionId) => {
    if (!editingSectionTitle.trim()) {
      toast.error("Section name cannot be empty");
      return;
    }

    if (isRenamingSection) return;

    setIsRenamingSection(true);

    try {
      const payload = {
        title: editingSectionTitle.trim(),
      };

      const { data } = await api.put(
        ENDPOINTS.UPDATE_SECTION_BY_ID(sectionId),
        payload
      );

      if (data.success) {
        toast.success("Section renamed successfully");

        // Refresh sections list
        const res = await api.get(ENDPOINTS.GET_ALL_SECTIONS);
        if (res.data.success) {
          setSections(res.data.sections);
        }

        // Exit edit mode
        setEditingSectionId(null);
        setEditingSectionTitle("");
      } else {
        toast.error(data.message || "Failed to rename section");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error renaming section");
    } finally {
      setIsRenamingSection(false);
    }
  };
  const handleDeleteSection = (sectionId) => {
    if (deletingSectionId) return;

    confirmAlert({
      title: "Confirm Delete",
      message:
        "Are you sure you want to delete this section?\n\nThis action cannot be undone.",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            setDeletingSectionId(sectionId);

            try {
              const { data } = await api.delete(
                ENDPOINTS.DELETE_SECTION_BY_ID(sectionId)
              );

              if (data.success) {
                toast.success("Section deleted successfully");

                // Remove from selected sections if it was selected
                setSelectedSectionIds((prev) =>
                  prev.filter((id) => id !== sectionId)
                );

                // Refresh sections list
                const res = await api.get(ENDPOINTS.GET_ALL_SECTIONS);
                if (res.data.success) {
                  setSections(res.data.sections);
                }
              } else {
                toast.error(data.message || "Failed to delete section");
              }
            } catch (err) {
              console.error(err);
              const errorMessage =
                err.response?.data?.message ||
                "Error deleting section. This section may be in use by channels or communities.";
              toast.error(errorMessage);
            } finally {
              setDeletingSectionId(null);
            }
          },
        },
        {
          label: "No",
          onClick: () => toast.info("Section deletion cancelled"),
        },
      ],
    });
  };

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const { data } = await api.get(ENDPOINTS.GET_ALL_SECTIONS);
        if (data.success) {
          setSections(data.sections);
        }
      } catch (err) {
        console.error("Failed to fetch sections", err);
      }
    };

    fetchSections();
  }, []);

  useEffect(() => {
    const resetForm = () => {
      setCommunityName("");
      setThemeColor("#3b0a59");
      setHasLiveSession(false);
      setSelectedSectionIds([]);
      setBannerPreview(null);
      setIconPreview(null);
    };

    const prefillFormForEdit = async () => {
      const { data } = await api.get(
        ENDPOINTS.GET_COMMUNITY_BY_ID(editCommunityId)
      );
      if (data.success) {
        setCommunityName(data.community.name);
        setThemeColor(data.community.theme_color);
        setHasLiveSession(data.community.has_live_session === 1 ? true : false);
        setSelectedSectionIds(data.community.section_ids);
        let banner = `${ENDPOINTS.IMAGE_BASE_URL}${data.community.banner}`;
        let icon = `${ENDPOINTS.IMAGE_BASE_URL}${data.community.icon}`;
        setBannerPreview(banner);
        setIconPreview(icon);
      }
    };

    if (editCommunityId !== null) {
      prefillFormForEdit();
    } else if (editCommunityId === null) {
      resetForm();
    }
  }, [editCommunityId]);

  const filteredSections = sections.filter((section) =>
    section.title.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div className={styles.pageWrapper}>
      {/* App Bar */}
      <div className={styles.appbarContainer}>
        <h2 className={styles.appbarTitle}>
          {editCommunityId === null ? "Create" : "Update"} Community
        </h2>
      </div>

      <div className={styles.bodyContainer}>
        {/* Left Section */}
        <div className={styles.leftSection}>
          {/* Image Upload */}
          <div className={styles.imageSectionWrapper}>
            {bannerPreview && (
              <div className={styles.featuredImageBox}>
                <img
                  src={bannerPreview}
                  alt="Banner Preview"
                  className={styles.featuredImageBox}
                />
                <div className={styles.iconUploadContainer}>
                  <img
                    src={iconPreview || placeholderImage}
                    alt="Icon Preview"
                    className={styles.iconPlaceholder}
                  />
                  <div className={styles.uploadLinks}>
                    <label className={styles.uploadLink}>
                      <span className={styles.plusIcon}>+</span> Choose Featured
                      Image
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleBannerUpload}
                      />
                    </label>
                    <label className={styles.uploadLink}>
                      <span className={styles.plusIcon}>+</span> Choose icon
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleIconUpload}
                      />
                    </label>
                  </div>
                </div>
              </div>
            )}
            {!bannerPreview && (
              <div className={styles.featuredImageBox}>
                <div className={styles.iconUploadContainer}>
                  <img
                    src={iconPreview || placeholderImage}
                    alt="Icon Preview"
                    className={styles.iconPlaceholder}
                  />
                  <div className={styles.uploadLinks}>
                    <label className={styles.uploadLink}>
                      <span className={styles.plusIcon}>+</span> Choose Featured
                      Image
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleBannerUpload}
                      />
                    </label>
                    <label className={styles.uploadLink}>
                      <span className={styles.plusIcon}>+</span> Choose icon
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleIconUpload}
                      />
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Community name */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Community Name</label>
            <input
              type="text"
              value={communityName}
              onChange={(e) => setCommunityName(e.target.value)}
              className={styles.textInput}
              disabled={isSubmitting}
            />
          </div>

          {/* Community Theme */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Community theme</label>
            <div className={styles.colorPickerWrapper}>
              <input
                type="color"
                value={themeColor}
                onChange={(e) => setThemeColor(e.target.value)}
                className={styles.colorInput}
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Live Sessions */}
          <div className={styles.liveSessionRow}>
            <label className={styles.inlineLabel}>Includes Live Sessions</label>

            <div className={styles.toggleWrapper}>
              <button
                className={`${styles.toggleOption} ${
                  hasLiveSession ? styles.activeToggle : ""
                }`}
                onClick={() => setHasLiveSession(true)}
                disabled={isSubmitting}
              >
                Yes
              </button>
              <button
                className={`${styles.toggleOption} ${
                  !hasLiveSession ? styles.activeToggle : ""
                }`}
                onClick={() => setHasLiveSession(false)}
                disabled={isSubmitting}
              >
                No
              </button>
            </div>
            <span className={styles.note}>
              <span className={styles.noteHighlight}>NB*</span>: This option
              will create a new section page for
              <br />
              live sessions in this community
            </span>
          </div>
        </div>

        {/* Right Section */}
        <div className={styles.rightSection}>
          {/* Sections Card */}
          <div className={styles.sectionsWrapper}>
            <label className={styles.inlineLabel}>Sections List</label>
            <div className={styles.sectionsCard}>
              <div className={styles.searchBar}>
                <input
                  type="text"
                  placeholder="type in to Filter sections"
                  className={styles.searchInput}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  disabled={isSubmitting}
                />
                <span className={styles.searchIcon}>🔍</span>
              </div>

              <div className={styles.sectionList}>
                {filteredSections.map((section) => (
                  <div className={styles.sectionRow} key={section.id}>
                    {/* Show input field when editing this section */}
                    {editingSectionId === section.id ? (
                      <div className={styles.editSectionContainer}>
                        <input
                          type="text"
                          value={editingSectionTitle}
                          onChange={(e) =>
                            setEditingSectionTitle(e.target.value)
                          }
                          className={styles.editSectionInput}
                          disabled={isRenamingSection}
                          autoFocus
                        />
                        <div className={styles.editActions}>
                          <button
                            className={styles.saveButton}
                            onClick={() => handleSaveRename(section.id)}
                            disabled={
                              isRenamingSection || !editingSectionTitle.trim()
                            }
                          >
                            {isRenamingSection ? "⟳" : "✓"}
                          </button>
                          <button
                            className={styles.cancelButton}
                            onClick={handleCancelEdit}
                            disabled={isRenamingSection}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <span className={styles.sectionLabel}>
                          {section.title}
                        </span>
                        <div className={styles.sectionActions}>
                          {/* Edit Button */}
                          <button
                            className={styles.editIconButton}
                            onClick={() => handleStartEdit(section)}
                            disabled={
                              isSubmitting || deletingSectionId === section.id
                            }
                            title="Rename section"
                          >
                            ✎
                          </button>
                          {/* NEW: Delete Button */}
                          <button
                            className={styles.deleteIconButton}
                            onClick={() => handleDeleteSection(section.id)}
                            disabled={
                              isSubmitting || deletingSectionId === section.id
                            }
                            title="Delete section"
                          >
                            {deletingSectionId === section.id ? "⟳" : "🗑"}
                          </button>
                          {/* Checkbox */}
                          <input
                            type="checkbox"
                            className={styles.checkbox}
                            checked={selectedSectionIds.includes(section.id)}
                            onChange={() => handleCheckboxChange(section)}
                            disabled={
                              isSubmitting || deletingSectionId === section.id
                            }
                          />
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              <div className={styles.addSection}>
                <input
                  type="text"
                  placeholder="Add new section here"
                  value={newSection}
                  onChange={(e) => setNewSection(e.target.value)}
                  className={styles.addInputNewSection}
                  disabled={isCreatingSection || isSubmitting}
                />
                <button
                  className={`${styles.addButton} ${
                    isCreatingSection ? styles.loading : ""
                  }`}
                  onClick={handleCreateSection}
                  disabled={
                    isCreatingSection || isSubmitting || !newSection.trim()
                  }
                >
                  {isCreatingSection ? "⟳" : "+"}
                </button>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            className={`${styles.submitButton} ${
              isSubmitting ? styles.submitting : ""
            }`}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span>
                  {editCommunityId === null ? "Creating..." : "Updating..."}
                </span>
                <span className={styles.submitIcon}>⟳</span>
              </>
            ) : (
              <>
                <span>Submit Community</span>
                <span className={styles.submitIcon}>＋</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateCommunityPage;
